import { FC, useEffect, useMemo, useState } from "react";
import MapComponent from "../../../../common/components/map/map.component";
import Column from "../../../../common/components/grid/column";
import Row from "../../../../common/components/grid/row";
import CardComponent from "../../../../common/components/card/card.component";
import BillingFormData from "./types/billing-form.data";
import MapMarker from "../../../../common/components/map/types/map-marker";
import MapRoute from "../../../../common/components/map/types/map-route";
import billingDataRouteRequestsFactory from "./factory/billing-data-route-requests.factory";
import SearchRoadRoutingResponse from "../../../../common/utils/search-road-route/search-road-routing.response";
import billingMapRoutesFactory from "./factory/billing-map-routes.factory";
import BillingsNode from "../../common/types/billings-node";
import billingRecalculateRequestFactory from "./factory/billing-recalculate-request.factory";
import billingDataFactory from "./factory/billing-data.factory";
import BillingDetailsComponent from "./billing-details/billing-details.component";
import ContractDetailsComponent from "./contract-details/contract-details.component";
import ButtonComponent from "../../../../common/components/button/button.component";
import billingSaveRequestFactory from "./factory/billing-save-request.factory";
import RelatedRailyTaxiBillingsComponent from "./related-billings/raily-taxi/related-raily-taxi-billings.component";
import AdditionalOptionsComponent from "./additional-options/additional-options.component";
import { useNavigate, useParams } from "react-router-dom";
import BillingCargoRailyAddRouteParams from "../../common/routes/types/billing-cargo-raily-add-route-params";
import billingsTranslationsHelper from "../../../../languages/billings-translations.helper";
import BillingFormDataValidationResult, {
  NodeValidationResult,
} from "./types/billing-form-data-validation-result";
import formValidationService from "../../../../common/utils/validation/form-validation.service";
import billingDataAddValidationService from "./billing-data-add-validation.service";
import notificationService from "../../../../common/utils/notification/notification.service";
import billingRoutesHelper from "../../common/routes/billing-routes.helper";
import HeadingComponent from "../../../../common/components/heading/heading.component";
import { useAppContext } from "../../../../context/app.context";
import billingBreadcrumbsHelper from "../../common/breadcrumbs/billings-breadcrumbs.helper";
import useDocumentTitle from "../../../../common/hooks/use-document-title";
import appTranslationsHelper from "../../../../languages/app-translations.helper";
import BillingsSummaryConfirmationComponent from "./billings-summary-confirmation.component";
import BillingsRouteDetailsFormComponent from "../../common/route-details/billings-route-details-form.component";
import billingsRouteDetailsHelper from "../../common/route-details/common/billings-route-details.helper";
import MessengerComponent from "../../../../common/components/messenger/messenger.component";
import billingsCargoRailyHelper from "./billings-cargo-raily.helper";
import billingsMapMarkersFactory from "../../common/map-markers/billings-map-markers.factory";
import searchRoadRoutingService from "../../../../common/utils/search-road-route/search-road-routing.service";
import useGpsCargoOrderRecords from "../../../../common/services/gps/cargo-order-records/use-gps-cargo-order-records";
import useAbort from "../../../../common/hooks/use-abort";
import GpsCargoOrderRecordsParams from "../../../../common/services/gps/cargo-order-records/gps-cargo-order-records-params";
import GpsCargoOrderRecord from "../../../../common/services/gps/cargo-order-records/gps-cargo-order-records";
import billingService from "../../../../common/services/billings/billings.service";
import BillingsOrderCargoRailyAddParams from "../../../../common/services/billings/order/cargo-raily/add/billings-order-cargo-raily-add-params";
import BillingsOrderCargoRailyRecalculateParams from "../../../../common/services/billings/order/cargo-raily/recalculate/billings-order-cargo-raily-recalculate-params";
import useRecalculateCargoRailyBilling from "../../../../common/services/billings/order/cargo-raily/recalculate/use-billings-order-cargo-raily-recalculate";
import BillingsOrderCargoRailyRecalculate from "../../../../common/services/billings/order/cargo-raily/recalculate/billings-order-cargo-raily-recalculate";
import billingRecalculateDataFactory from "./factory/billing-recalculate-data.factory";
import BillingsOrderCargoRailyDraft from "../../../../common/services/billings/order/cargo-raily/draft/billings-order-cargo-raily-draft";
import useCargoRailyBillingDraft from "../../../../common/services/billings/order/cargo-raily/draft/use-billings-order-cargo-raily-draft";
import BillingsOrderCargoRailyDraftParams from "../../../../common/services/billings/order/cargo-raily/draft/billings-order-cargo-raily-draft-params";

type BillingsCargoRailyAddProps = {};

const BillingsCargoRailyAddComponent: FC<BillingsCargoRailyAddProps> = () => {
  const { orderUuid } = useParams<BillingCargoRailyAddRouteParams>();

  const { setBreadcrumbs, selectedAppLanguage } = useAppContext();

  const [orderInternalId, setOrderInternalId] = useState("");
  const [billingData, setBillingData] = useState<BillingFormData>();
  const [billingFormData, setBillingFormData] = useState<BillingFormData>();

  const [mapMarkers, setMapMarkers] = useState<MapMarker[]>();
  const [plannedMapRoute, setPlannedMapRoute] = useState<MapRoute | null>(null);
  const [completedMapRoute, setCompletedMapRoute] = useState<MapRoute | null>(
    null,
  );

  const gpsCargoOrderRecords = useGpsCargoOrderRecords();
  const gpsCargoOrderRecordsAbort = useAbort();

  const recalculateCargoRailyBilling = useRecalculateCargoRailyBilling();
  const recalculateCargoRailyBillingAbort = useAbort();

  const billingsOrderCargoRailyAddAbort = useAbort();

  const cargoRailyBillingDraft = useCargoRailyBillingDraft();
  const cargoRailyBillingDraftAbort = useAbort();

  const [isBillingSavePending, setIsBillingSavePending] = useState(false);

  const [isSummaryConfirmationVisible, setIsSummaryConfirmationVisible] =
    useState(false);
  const [shouldRetrieveRouteData, setShouldRetrieveRouteData] = useState(false);
  const [formValidationResults, setFormValidationResults] =
    useState<BillingFormDataValidationResult>({
      discountValue: formValidationService.defaultValidationResult,
      nodeValidations: [],
    });

  const documentTitle = appTranslationsHelper
    .getDocumentTitleTranslations()
    .billingsCargoWithRailyAdd.replace("#{orderInternalId}", orderInternalId);

  useDocumentTitle(documentTitle);

  useEffect(() => {
    const breadcrumbs =
      billingBreadcrumbsHelper.getCargoWithRailyAddBreadcrumbs({
        orderUuid: orderUuid!,
        orderInternalId,
      });

    setBreadcrumbs(breadcrumbs);
  }, [selectedAppLanguage, orderInternalId]);

  useEffect(() => {
    if (!orderUuid) {
      return;
    }

    const params: BillingsOrderCargoRailyDraftParams = {
      orderUuid: orderUuid,
    };

    cargoRailyBillingDraft.load(params, cargoRailyBillingDraftAbort.signal);

    return cargoRailyBillingDraftAbort.revoke;
  }, [orderUuid]);

  useEffect(() => {
    if (!cargoRailyBillingDraft.data) {
      return;
    }

    onBillingDataFetchSuccess(cargoRailyBillingDraft.data);
  }, [cargoRailyBillingDraft.data]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!orderUuid) return;

    const params: GpsCargoOrderRecordsParams = {
      orderUuid,
    };

    gpsCargoOrderRecords.load(params, gpsCargoOrderRecordsAbort.signal);

    return gpsCargoOrderRecordsAbort.revoke;
  }, [orderUuid]);

  useEffect(() => {
    if (!gpsCargoOrderRecords.data) {
      return;
    }
    onGpsDataFetchSuccess(gpsCargoOrderRecords.data);
  }, [gpsCargoOrderRecords.data]);

  useEffect(() => {
    if (!billingData || !shouldRetrieveRouteData) {
      return;
    }

    const mapMarkers = billingsMapMarkersFactory.createMapMarkers(
      billingData.billingNodes,
    );

    const routeRequests =
      billingDataRouteRequestsFactory.createBillingDataRouteRequests(
        billingData.billingNodes,
      );

    const fetchPromises: Promise<SearchRoadRoutingResponse>[] = [];

    routeRequests.forEach((routeRequest) => {
      const fetch = searchRoadRoutingService.search(routeRequest);

      fetchPromises.push(fetch);
    });

    Promise.all(fetchPromises).then((responses) => {
      const mapRouteWaypointGroups: MapRoute["waypoints"][] = [];

      responses.forEach((response) => {
        const mapRoute = response.routes[0]
          ? billingMapRoutesFactory.createMapRoute(
              response.routes[0].geometry.coordinates,
            )
          : null;

        if (mapRoute?.waypoints) {
          mapRouteWaypointGroups.push(mapRoute.waypoints);
        }
      });

      const newMapRouteWaypoints: MapRoute["waypoints"] = [];

      mapRouteWaypointGroups.forEach((waypoint) => {
        newMapRouteWaypoints.push(...waypoint);
      });

      const newMapRoute: MapRoute = {
        waypoints: newMapRouteWaypoints,
        options: { color: "red" },
      };

      setPlannedMapRoute(newMapRoute);
      setMapMarkers(mapMarkers);
    });
  }, [shouldRetrieveRouteData]);

  const recalculateBilling = (formData: BillingFormData) => {
    const params: BillingsOrderCargoRailyRecalculateParams =
      billingRecalculateRequestFactory.createRecalculateRequest(formData!);

    recalculateCargoRailyBilling.load(
      params,
      recalculateCargoRailyBillingAbort.signal,
    );
  };

  useEffect(() => {
    if (!recalculateCargoRailyBilling.data) {
      return;
    }

    onRecalculateSuccess(recalculateCargoRailyBilling.data);
  }, [recalculateCargoRailyBilling.data]);

  const onBillingAddSuccess = () => {
    notificationService.success(translations.successNotificationText);
    navigateToListing();
  };

  const onBillingAddFailure = () => {
    notificationService.error(translations.failureNotificationText);
  };

  const navigateToListing = () => {
    navigate(billingRoutesHelper.getRailyUnbilledOrderListingRoute());
  };

  const onModalClose = () => {
    setIsSummaryConfirmationVisible(false);
  };

  const openModal = () => {
    setIsSummaryConfirmationVisible(true);
  };

  const onBillingDataFetchSuccess = (
    response: BillingsOrderCargoRailyDraft,
  ) => {
    const billingData = billingDataFactory.createBillingData(response);

    setBillingData(billingData);
    setOrderInternalId(String(response.cargoOrder.humanId));
    setBillingFormData(billingData);
    setShouldRetrieveRouteData(true);

    setFormValidationResults((current) => ({
      discountValue: current.discountValue,
      nodeValidations: billingData.billingNodes.map((x) => {
        return {
          distance: formValidationService.defaultValidationResult,
          haltingTime: formValidationService.defaultValidationResult,
          highwayCharge: formValidationService.defaultValidationResult,
          position: x.position,
        };
      }),
    }));
  };

  const validationOnBlur = (position: number, fieldName: string) => {
    type Node = keyof typeof node;

    const node = billingFormData?.billingNodes.find(
      (x) => x.position === position,
    );

    const fieldToValidate = node && node[fieldName as Node];

    const results = billingsRouteDetailsHelper.validationOnBlur(
      fieldToValidate,
      formValidationResults,
      position,
      fieldName,
    );

    nodeValidationResults(results.nodeResult!);

    if (results.isAllFormValid) recalculateBilling(billingFormData!);
  };

  const nodeValidationResults = (node: NodeValidationResult[]) => {
    setFormValidationResults((curr) => ({
      ...curr,
      nodeValidations: node,
    }));
  };

  const onGpsDataFetchSuccess = (response: GpsCargoOrderRecord[]) => {
    const gpsData: MapRoute = {
      waypoints: response.map((x) => {
        return { latitude: x.lat, longitude: x.lon };
      }),
      options: { color: "blue" },
    };

    setCompletedMapRoute(gpsData);
  };

  const areNodesValid = () => {
    return (
      billingFormData?.billingNodes
        .map((node) => {
          return billingsRouteDetailsHelper.validateHaltingTime(
            formValidationResults,
            node.haltingTime,
            node.position,
          );
        })
        .every((x) => x) ?? false
    );
  };

  const onBillingDataSave = () => {
    const isDiscountValid = validateDiscount();
    const isFormValid = isDiscountValid && areNodesValid();

    if (!isFormValid) return;

    if (billingFormData) {
      setIsBillingSavePending(true);

      const params: BillingsOrderCargoRailyAddParams =
        billingSaveRequestFactory.createSaveBillingRequest(billingFormData);

      billingService
        .addOrderCargoRailyBillings(
          params,
          billingsOrderCargoRailyAddAbort.signal,
        )
        .then(onBillingAddSuccess)
        .catch(onBillingAddFailure)
        .finally(() => setIsBillingSavePending(false));
    }
  };

  const onDiscountBlur = () => {
    const isDiscountValid = validateDiscount();

    if (isDiscountValid && areNodesValid()) {
      recalculateBilling(billingFormData!);
    }
  };

  const validateDiscount = () => {
    const maximumDiscountValue = billingData?.contractDetails?.discountLimit
      ? billingData?.contractDetails?.discountLimit
      : 0;

    const validationResult = billingDataAddValidationService.validateDiscount(
      billingFormData?.discount ?? 0,
      maximumDiscountValue,
    );

    setFormValidationResults((curr) => ({
      ...curr,
      discountValue: validationResult,
    }));

    return validationResult.isValid;
  };

  const onBillingNodeChanged = (node: BillingsNode) => {
    const nodes = billingFormData?.billingNodes.filter(
      (x) => x.position !== node.position,
    )!;

    nodes.push(node);

    const sortedArray = nodes.sort((a, b) =>
      a.position < b.position ? -1 : 1,
    );

    setBillingFormData((current) => ({
      ...current!,
      billingNodes: sortedArray,
    }));
  };

  const onCopyFromPlannedDistance = () => {
    const newBillingNodes = billingFormData?.billingNodes.map((node) => {
      node.distance = node.plannedDistance;

      return node;
    });

    const newBillingFormData: BillingFormData = {
      ...billingFormData!,
      billingNodes: newBillingNodes!,
    };

    setBillingFormData(newBillingFormData);

    const result = billingsRouteDetailsHelper.validateAllFieldsByType(
      formValidationResults,
      newBillingFormData.billingNodes,
      "distance",
    );

    nodeValidationResults(result.nodeResult!);

    if (result.isAllFormValid) recalculateBilling(newBillingFormData);
  };

  const onDiscountChanged = (discountValue: number) => {
    setBillingFormData((current) => ({ ...current!, discount: discountValue }));
  };

  const onInputBlur = () => {
    if (!areNodesValid()) return;

    recalculateBilling(billingFormData!);
  };

  const onRecalculateSuccess = (
    response: BillingsOrderCargoRailyRecalculate,
  ) => {
    const billingData =
      billingRecalculateDataFactory.createBillingRecalculateData(response);
    setBillingData(billingData);
  };

  const mapRoutes: MapRoute[] = useMemo(() => {
    const finalMapRoutes: MapRoute[] = [];
    if (plannedMapRoute) {
      finalMapRoutes.push(plannedMapRoute);
    }
    if (completedMapRoute) {
      finalMapRoutes.push(completedMapRoute);
    }
    return finalMapRoutes;
  }, [plannedMapRoute, completedMapRoute]);

  const translations =
    billingsTranslationsHelper.getCargoRailyAddBillingsTranslations();

  const messengerChannelsAvailability =
    billingsCargoRailyHelper.getMessengerChannelAvailability();

  return (
    <>
      <div className="billings">
        <HeadingComponent
          title={translations.header.headingText.replace(
            "#{orderInternalId}",
            orderInternalId,
          )}
        />
        <Row>
          <Column lg={8}>
            <Row>
              <Column withPaddings>
                <div className="billings_map_wrapper">
                  <MapComponent
                    markers={mapMarkers}
                    autoFit
                    autoFitOnUpdate
                    routes={mapRoutes}
                  />
                </div>
              </Column>
              <Column withPaddings>
                <ContractDetailsComponent
                  isLoading={cargoRailyBillingDraft.isLoading}
                  contractDetails={billingData?.contractDetails ?? null}
                />
              </Column>
              <Column withPaddings>
                <Row>
                  <Column lg={9}>
                    <BillingsRouteDetailsFormComponent
                      billingNodes={billingFormData?.billingNodes}
                      formValidationResults={
                        formValidationResults.nodeValidations
                      }
                      orderId={orderUuid!}
                      onBillingNodeChanged={onBillingNodeChanged}
                      onInputBlur={onInputBlur}
                      onCopyFromPlannedDistance={onCopyFromPlannedDistance}
                      validationOnBlur={validationOnBlur}
                    />
                  </Column>
                  <Column lg={3}>
                    <AdditionalOptionsComponent
                      billingData={billingFormData}
                      onDiscountChanged={onDiscountChanged}
                      onDiscountBlur={onDiscountBlur}
                      formValidationResults={formValidationResults}
                    />
                  </Column>
                </Row>
              </Column>
              <Column withPaddings>
                <CardComponent
                  classNames={{ root: "billing_details" }}
                  header={{ title: translations.billingSummary.summaryLabel }}
                >
                  <BillingDetailsComponent billingData={billingData} />
                </CardComponent>
              </Column>
              <Column>
                <ButtonComponent type="primary" onClick={openModal}>
                  {translations.submitLabel}
                </ButtonComponent>
              </Column>
              <Column withPaddings>
                <RelatedRailyTaxiBillingsComponent
                  orderId={billingData?.cargoOrderId}
                />
              </Column>
            </Row>
          </Column>
          <Column lg={4} withPaddings>
            {!!billingData && (
              <CardComponent classNames={{ root: "h-50", content: "h-100" }}>
                <MessengerComponent
                  channelsAvailability={messengerChannelsAvailability}
                  orderUuid={orderUuid!}
                  planEntryUuid={billingData.planEntryId}
                />
              </CardComponent>
            )}
          </Column>
        </Row>
        <BillingsSummaryConfirmationComponent
          isVisible={isSummaryConfirmationVisible}
          onSubmit={onBillingDataSave}
          onClose={onModalClose}
          billingData={billingData!}
          isLoading={isBillingSavePending}
        />
      </div>
    </>
  );
};

export default BillingsCargoRailyAddComponent;
